/**
 * Filters a schedule object to only include lessons matching the given location ID.
 *
 * @param {object} schedule - The full schedule response from the API ({ objects: { [hour]: lesson[] } })
 * @param {number|null} locationId - The location ID to filter by, or null to return all lessons
 * @returns {object} A new schedule object with the same shape but only matching lessons
 */
export const filterScheduleByLocationId = (schedule, locationId) => {
  if (!schedule || !schedule.objects) return schedule;
  if (locationId === null || locationId === undefined) return schedule;

  const filteredObjects = {};

  Object.keys(schedule.objects).forEach((hourKey) => {
    const hourLessons = schedule.objects[hourKey];

    if (!Array.isArray(hourLessons)) {
      // null slots (gaps) — omit in filtered view
      return;
    }

    const matching = hourLessons.filter(
      (lesson) =>
        lesson.lesson_type?.location?.id === locationId ||
        lesson.lesson_type?.location?.available_everywhere === true
    );

    if (matching.length > 0) {
      filteredObjects[hourKey] = matching;
    }
  });

  return {
    ...schedule,
    objects: filteredObjects,
    num_results: Object.keys(filteredObjects).length,
  };
};

/**
 * Picks the all-day events covering a single day of the timetable.
 *
 * Comparison is done on the YYYY-MM-DD prefix of the API timestamps, so a
 * timezone offset can never shift an event onto the neighbouring day.
 *
 * @param {Array} events - all-day events from `GET /events/all-day`
 * @param {string} isoDate - the day to test, as YYYY-MM-DD
 * @returns {Array} events covering that day
 */
export const getAllDayEventsForDate = (events, isoDate) => {
  if (!Array.isArray(events) || !isoDate) return [];

  return events.filter((event) => {
    const start = (event.start_date || "").slice(0, 10);
    const end = (event.end_date || start).slice(0, 10);
    return start && start <= isoDate && isoDate <= end;
  });
};
